<template>
    <div
        :style="styles.card"
        :class="selectedClasses"
        @click="onCardClicked"
        class="base-card"
    >
        <div ref="player" class="music-player">
            <slot name="music-player"></slot>
        </div>
        <img :src="image" :style="styles.image" alt="card-image" class="card-image" />
        <div :style="styles.content" class="card-content">
            <div :style="styles.contentMain" class="content-main">
                <slot name="content-main"></slot>
            </div>
            <div :style="styles.contentFooter" class="content-footer">
                <div :style="styles.footerText" class="footer-text">
                    <slot name="footer-text"></slot>
                </div>
                <button
                    ref="cta"
                    v-if="showCta"
                    :style="styles.cta"
                    :disabled="cta.disabled"
                    @click="$emit('cta-clicked')"
                    class="cta-btn"
                >
                    {{ cta.text }}
                </button>
            </div>
        </div>
    </div>
</template>
<script>
    /**
     *  BaseCard: A base card for the app
     *
     *  @prop {Boolean} selectable
     *  @prop {Boolean} clickable
     *  @prop {Boolean} clear
     *  @prop {String} image
     *  @prop {Object} cta
     *  @prop {Object} styles
     *
     *  @slot music-player
     *  @slot content-main (flexed column)
     *  @slot footer-text (flexed column)
     *
     *  @emits cta-clicked
     *  @emits card-clicked @param {Boolean} this.selected
     */
    const defaultImage = '/images/default-artist-event.png'
    export default {
        name: 'BaseCard',
        props: {
            clear: {
                type: Boolean,
                required: false,
                default: false
            },
            selectable: {
                type: Boolean,
                required: false,
                default: false
            },
            clickable: {
                type: Boolean,
                required: false,
                default: false
            },
            image: {
                type: String,
                required: false,
                default: defaultImage
            },
            cta: {
                type: Object,
                required: false,
                default: () => ({
                    text: 'CTA',
                    disabled: false
                })
            },
            showCta: {
                type: Boolean,
                required: false,
                default: true
            },
            styles: {
                type: Object,
                required: false,
                default: () => ({
                    card: {},
                    image: {},
                    content: {},
                    contentMain: {},
                    contentFooter: {},
                    footerText: {},
                    cta: {}
                })
            }
        },
        emits: ['cta-clicked', 'card-clicked'],
        data: () => ({
            selected: false
        }),
        computed: {
            selectedClasses: (self) => ({
                selectable: self.selectable,
                selected: self.selected,
                clickable: self.clickable
            }),
            cardAreaClicked: (self) => (e) =>
                !self.$refs.player.contains(e.target) && e.target !== self.$refs.cta
        },
        watch: {
            clear: {
                handler: function (clear) {
                    if (clear && this.selected && this.$refs.cta) {
                        this.$refs.cta.click()
                    }
                },
                immediate: true
            }
        },
        methods: {
            onCardClicked(event) {
                if (this.selectable || this.clickable) {
                    if (this.selectable) this.selected = !this.selected
                    if (this.cardAreaClicked(event)) this.$emit('card-clicked', this.selected)
                }
            }
        }
    }
</script>
<style lang="scss" scoped>
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    button:hover {
        background: #dedede !important;
    }
    button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .selectable:hover {
        border: 1px solid #cdeb09;
    }
    .selectable,
    .clickable {
        cursor: pointer;
    }
    .selected {
        border: 1px solid #cdeb09 !important;
    }
    .base-card {
        width: 100%;
        max-width: 343px;
        height: 100%;
        border-radius: 24px;
        border: 1px solid #333;
        background: #262626;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        flex-shrink: 0;

        .card-image {
            width: 100%;
            height: 192px;
            background: #333;
            object-fit: cover;
            border-top-left-radius: 24px;
            border-top-right-radius: 24px;
        }
        .card-content {
            width: 100%;
            height: 100%;
            background: inherit;
            padding: 16px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
            gap: 24px;
            border-bottom-left-radius: 24px;
            border-bottom-right-radius: 24px;

            .content-main {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 8px;
            }
            .content-footer {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;

                .footer-text {
                    width: 100%;
                    min-height: 48px;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: center;
                }

                .cta-btn {
                    width: 100%;
                    max-width: 135px;
                    display: flex;
                    justify-content: center;
                    padding: 4px 16px;
                    text-wrap: nowrap;
                    border-radius: 24px;
                    background: #cdeb09;
                    color: #262626;
                    font-variant-numeric: ordinal;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 24px;
                }
            }
        }
    }
</style>
